import React, { useState, useMemo } from "react";
import { Button, Modal, Table, Form } from "react-bootstrap";
import moment from "moment";
import PerfectScrollbar from "react-perfect-scrollbar";

import { toPesoAmount } from "../../helpers";
import useFetch from "../../hooks/useFetch";
import PacmanWrapper from "./PacmanWrapper";
import { DB_DATE_FORMAT } from "../../constants";

const toNum = (val) => {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};

const exportToCSV = (items, filename, excludeExtras = false) => {
  if (!items || items.length === 0) return;

  try {
    const totalSalesAll = items.reduce(
      (acc, cur) => acc + toNum(cur.totalSales),
      0,
    );
    const totalQtyAll = items.reduce((acc, cur) => acc + toNum(cur.soldQty), 0);
    const totalBaseSalesAll = items.reduce(
      (acc, cur) => acc + toNum(cur.baseSales),
      0,
    );
    const totalExtrasSalesAll = items.reduce(
      (acc, cur) => acc + toNum(cur.extrasSales),
      0,
    );

    const headers = [
      "Rank",
      "Item Name",
      "Category",
      "Item Code",
      "Quantity Sold",
      excludeExtras
        ? "Total Sales (Excl. Extras) (PHP)"
        : "Total Sales (Incl. Extras) (PHP)",
      "Base Sales (PHP)",
      "Extras Sales (PHP)",
      "Percentage of Total (%)",
    ];

    const escapeCell = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const rows = items.map((item, idx) => {
      const rank = idx + 1;
      const name = item.item_details?.name || "Unknown Item";
      const category =
        item.category || item.item_details?.category || "Uncategorized";
      const itemCode = item.item_details?.item_code || item._id || "";
      const qty = toNum(item.soldQty);
      const sales = toNum(item.totalSales).toFixed(2);
      const baseSales = toNum(
        item.baseSales !== undefined ? item.baseSales : item.totalSales,
      ).toFixed(2);
      const extrasSales = toNum(item.extrasSales).toFixed(2);
      const percentage =
        totalSalesAll > 0
          ? ((toNum(item.totalSales) / totalSalesAll) * 100).toFixed(2)
          : "0.00";

      return [
        escapeCell(rank),
        escapeCell(name),
        escapeCell(category),
        escapeCell(itemCode),
        escapeCell(qty),
        escapeCell(sales),
        escapeCell(baseSales),
        escapeCell(extrasSales),
        escapeCell(percentage),
      ].join(",");
    });

    const summaryRow = [
      escapeCell("TOTAL"),
      escapeCell(`${items.length} items`),
      escapeCell(""),
      escapeCell(""),
      escapeCell(totalQtyAll),
      escapeCell(totalSalesAll.toFixed(2)),
      escapeCell(totalBaseSalesAll.toFixed(2)),
      escapeCell(totalExtrasSalesAll.toFixed(2)),
      escapeCell("100.00"),
    ].join(",");

    const csvContent = [headers.join(","), ...rows, summaryRow].join("\r\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  } catch (err) {
    console.error("Failed to export CSV:", err);
  }
};

const getDateRangeText = (params = {}) => {
  const { dateRange = "day", date = moment().format(DB_DATE_FORMAT) } = params;

  if (dateRange === "between") {
    if (params.endDate) {
      return `${moment(date).format("MMM DD, YYYY")} - ${moment(
        params.endDate,
      ).format("MMM DD, YYYY")}`;
    }
    return "Custom Range";
  }

  if (dateRange === "allTime") return "All-time";

  const isCurrent = moment(moment().format(DB_DATE_FORMAT)).isSame(
    date,
    dateRange,
  );
  const prefix = isCurrent ? "This" : "Last";

  if (dateRange === "week") {
    if (
      moment(moment().add(1, "weeks").format(DB_DATE_FORMAT)).isSame(
        date,
        "week",
      )
    ) {
      return "Next Week";
    }
    return `${prefix} Week`;
  }
  if (dateRange === "month") return `${prefix} Month`;
  if (dateRange === "year") return `${prefix} Year`;

  if (
    moment(moment().subtract(1, "days").format(DB_DATE_FORMAT)).isSame(
      date,
      "day",
    )
  ) {
    return "Yesterday";
  }

  return isCurrent ? "Today" : moment(date).format("MMM DD, YYYY");
};

const SalesPerItemCard = ({ params = {}, className = "" }) => {
  const [showModal, setShowModal] = useState(false);
  const [excludeExtras, setExcludeExtras] = useState(false);

  const reqObj = {
    url: "/dashboard/sales-per-item",
    method: "post",
    body: {
      ...params,
      sortBy: "totalSales",
      excludeReservation: true,
      excludeExtras,
    },
  };

  const { data, isLoading } = useFetch(reqObj, {}, [
    params.date,
    params.dateRange,
    params.id,
    params.attr,
    params.source_filter,
    excludeExtras,
  ]);

  const items = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const totalSalesAll = useMemo(
    () => items.reduce((acc, cur) => acc + toNum(cur.totalSales), 0),
    [items],
  );

  const totalQtyAll = useMemo(
    () => items.reduce((acc, cur) => acc + toNum(cur.soldQty), 0),
    [items],
  );

  const handleExport = (customItems) => {
    const exportItems = Array.isArray(customItems) ? customItems : items;
    if (!exportItems || exportItems.length === 0) return;

    const extrasSuffix = excludeExtras ? "-excl-extras" : "-incl-extras";
    const cleanDate = (params.date || moment().format(DB_DATE_FORMAT)).replace(
      /[/\\?%*:|"<>]/g,
      "-",
    );
    const cleanRange = (params.dateRange || "day").replace(
      /[/\\?%*:|"<>]/g,
      "-",
    );
    const filename = `sales-per-item-${cleanRange}-${cleanDate}${extrasSuffix}.csv`;
    exportToCSV(exportItems, filename, excludeExtras);
  };

  return (
    <>
      <SalesPerItemModal
        show={showModal}
        onHide={() => setShowModal(false)}
        items={items}
        params={params}
        totalSalesAll={totalSalesAll}
        totalQtyAll={totalQtyAll}
        onExport={handleExport}
        excludeExtras={excludeExtras}
        setExcludeExtras={setExcludeExtras}
        isLoading={isLoading}
      />

      {isLoading || data === null ? (
        <PacmanWrapper />
      ) : (
        <div
          className={`card card-custom d-flex ${className}`}
          style={{ fontSize: ".9rem" }}
        >
          {/* Header following SalesByFulfillmentCard style */}
          <div className="d-flex justify-content-between align-items-center pt-8 px-8 pb-4">
            <div className="d-flex align-items-center">
              <h4 className="m-0">Sales per Item</h4>
              <Button
                variant="link"
                className="ml-2"
                id="sales-per-item-export-btn"
                onClick={(e) => {
                  if (e && e.preventDefault) e.preventDefault();
                  handleExport();
                }}
                disabled={items.length === 0 || isLoading}
              >
                Generate Report
              </Button>
            </div>
            <div className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="sales-per-item-exclude-extras-switch"
                label="Exclude Extras"
                className="mr-4 font-weight-bold"
                checked={excludeExtras}
                onChange={(e) => setExcludeExtras(e.target.checked)}
              />
              <Button
                variant="link"
                className="p-0 font-weight-bold"
                onClick={() => setShowModal(true)}
              >
                View All ({items.length})
              </Button>
            </div>
          </div>

          {/* Body with Tabular Data */}
          <div className="card-body pt-0 px-8 pb-6 align-self-stretch">
            {items.length === 0 ? (
              <p className="text-center m-0 mt-12 mb-12">
                We don't have any records yet.
              </p>
            ) : (
              <>
                <div className="table-responsive">
                  <Table striped bordered hover className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "50px", textAlign: "center" }}>
                          #
                        </th>
                        <th>Item</th>
                        <th>Category</th>
                        <th style={{ textAlign: "right" }}>Quantity Sold</th>
                        <th style={{ textAlign: "right" }}>
                          Total Sales
                          <small className="text-muted d-block font-weight-normal font-size-xs">
                            {excludeExtras
                              ? "(excl. extras)"
                              : "(incl. extras)"}
                          </small>
                        </th>
                        <th style={{ textAlign: "right" }}>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.slice(0, 5).map((item, idx) => {
                        const sharePct =
                          totalSalesAll > 0
                            ? ((item.totalSales || 0) / totalSalesAll) * 100
                            : 0;

                        return (
                          <tr key={item._id || idx}>
                            <td
                              style={{ textAlign: "center" }}
                              className="font-weight-bold text-muted align-middle"
                            >
                              {idx + 1}
                            </td>
                            <td className="align-middle">
                              <div className="d-flex align-items-center">
                                <div
                                  className="symbol symbol-35 mr-3 flex-shrink-0"
                                  style={{
                                    overflow: "hidden",
                                    borderRadius: "6px",
                                  }}
                                >
                                  {item.item_details?.image_url ? (
                                    <img
                                      className="img-fluid"
                                      src={item.item_details.image_url}
                                      alt={item.item_details?.name}
                                      style={{
                                        width: "35px",
                                        height: "35px",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center bg-light text-muted font-weight-bold"
                                      style={{ width: "35px", height: "35px" }}
                                    >
                                      {(item.item_details?.name || "I")[0]}
                                    </div>
                                  )}
                                </div>
                                <span
                                  className="font-weight-bold text-dark text-hover-primary"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => setShowModal(true)}
                                >
                                  {item.item_details?.name || "Unknown Item"}
                                </span>
                              </div>
                            </td>
                            <td className="align-middle">
                              <span className="badge badge-light-primary font-weight-bold">
                                {item.category ||
                                  item.item_details?.category ||
                                  "Uncategorized"}
                              </span>
                            </td>
                            <td
                              style={{ textAlign: "right" }}
                              className="font-weight-bold text-dark-75 align-middle"
                            >
                              {(item.soldQty || 0).toLocaleString()}
                            </td>
                            <td
                              style={{ textAlign: "right" }}
                              className="font-weight-bold text-primary align-middle"
                            >
                              {toPesoAmount(item.totalSales || 0)}
                            </td>
                            <td
                              style={{ textAlign: "right" }}
                              className="font-weight-bold text-muted align-middle"
                            >
                              {sharePct.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {items.length > 0 && (
                      <tfoot className="font-weight-bold bg-light">
                        <tr>
                          <td colSpan="3" className="text-right">
                            Total:
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {totalQtyAll.toLocaleString()}
                          </td>
                          <td
                            style={{ textAlign: "right" }}
                            className="text-primary font-weight-bolder"
                          >
                            {toPesoAmount(totalSalesAll)}
                          </td>
                          <td style={{ textAlign: "right" }}>100.0%</td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>

                {items.length > 5 && (
                  <div className="text-center pt-4">
                    <Button
                      variant="link"
                      className="font-weight-bold text-primary p-0"
                      onClick={() => setShowModal(true)}
                    >
                      View All {items.length} Items in Modal →
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SalesPerItemCard;

const SalesPerItemModal = ({
  show,
  onHide,
  items = [],
  params = {},
  totalSalesAll = 0,
  totalQtyAll = 0,
  onExport,
  excludeExtras = false,
  setExcludeExtras,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("totalSales");
  const [sortOrder, setSortOrder] = useState("desc");
  const dateText = getDateRangeText(params);

  const filteredAndSortedItems = useMemo(() => {
    let result = [...items];

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter((item) => {
        const name = (item.item_details?.name || "").toLowerCase();
        const code = (
          item.item_details?.item_code ||
          item._id ||
          ""
        ).toLowerCase();
        const category = (
          item.category ||
          item.item_details?.category ||
          ""
        ).toLowerCase();
        return name.includes(q) || code.includes(q) || category.includes(q);
      });
    }

    result.sort((a, b) => {
      let valA, valB;
      if (sortField === "name") {
        valA = (a.item_details?.name || "").toLowerCase();
        valB = (b.item_details?.name || "").toLowerCase();
        return sortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortField === "category") {
        valA = (a.category || a.item_details?.category || "").toLowerCase();
        valB = (b.category || b.item_details?.category || "").toLowerCase();
        return sortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortField === "soldQty") {
        valA = a.soldQty || 0;
        valB = b.soldQty || 0;
      } else {
        // totalSales
        valA = toNum(a.totalSales);
        valB = toNum(b.totalSales);
      }

      return sortOrder === "asc" ? valA - valB : valB - valA;
    });

    return result;
  }, [items, searchTerm, sortField, sortOrder]);

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="text-muted ml-1 opacity-50">↕</span>;
    }
    return (
      <span className="text-primary font-weight-bold ml-1">
        {sortOrder === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Sales per Item on {dateText}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h4 className="m-0 mr-4">
            Total Sales: {toPesoAmount(totalSalesAll)}{" "}
            <span className="text-muted font-size-sm font-weight-normal ml-2">
              ({totalQtyAll.toLocaleString()} units sold across {items.length}{" "}
              items &bull;{" "}
              {excludeExtras ? "excluding extras" : "including extras"})
            </span>
          </h4>
          <div className="d-flex align-items-center mt-2 mt-sm-0">
            {setExcludeExtras && (
              <Form.Check
                type="switch"
                id="modal-sales-per-item-exclude-extras-switch"
                label="Exclude Extras"
                className="mr-4 font-weight-bold"
                checked={excludeExtras}
                onChange={(e) => setExcludeExtras(e.target.checked)}
              />
            )}
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search item or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "240px" }}
              className="mr-3"
            />
            <Button
              variant="link"
              id="sales-per-item-modal-export-btn"
              onClick={(e) => {
                if (e && e.preventDefault) e.preventDefault();
                if (onExport) onExport(filteredAndSortedItems);
              }}
              disabled={filteredAndSortedItems.length === 0 || isLoading}
            >
              Generate Report
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-12">
            <PacmanWrapper />
          </div>
        ) : (
          <PerfectScrollbar style={{ maxHeight: "500px" }}>
            <Table striped bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: "50px", textAlign: "center" }}>#</th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSortChange("name")}
                  >
                    Item Name {getSortIcon("name")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSortChange("category")}
                  >
                    Category {getSortIcon("category")}
                  </th>
                  <th
                    style={{ cursor: "pointer", textAlign: "right" }}
                    onClick={() => handleSortChange("soldQty")}
                  >
                    Quantity Sold {getSortIcon("soldQty")}
                  </th>
                  <th
                    style={{ cursor: "pointer", textAlign: "right" }}
                    onClick={() => handleSortChange("totalSales")}
                  >
                    Total Sales {getSortIcon("totalSales")}
                    <small className="text-muted d-block font-weight-normal font-size-xs">
                      {excludeExtras ? "(excl. extras)" : "(incl. extras)"}
                    </small>
                  </th>
                  <th style={{ textAlign: "right" }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedItems.length > 0 ? (
                  filteredAndSortedItems.map((item, idx) => {
                    const sharePct =
                      totalSalesAll > 0
                        ? ((item.totalSales || 0) / totalSalesAll) * 100
                        : 0;

                    return (
                      <tr key={item._id || idx}>
                        <td
                          style={{ textAlign: "center" }}
                          className="font-weight-bold text-muted align-middle"
                        >
                          {idx + 1}
                        </td>
                        <td className="align-middle">
                          <div className="d-flex align-items-center">
                            <div
                              className="symbol symbol-35 mr-3 flex-shrink-0"
                              style={{
                                overflow: "hidden",
                                borderRadius: "6px",
                              }}
                            >
                              {item.item_details?.image_url ? (
                                <img
                                  className="img-fluid"
                                  src={item.item_details.image_url}
                                  alt={item.item_details?.name}
                                  style={{
                                    width: "35px",
                                    height: "35px",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div
                                  className="d-flex align-items-center justify-content-center bg-light text-muted font-weight-bold"
                                  style={{ width: "35px", height: "35px" }}
                                >
                                  {(item.item_details?.name || "I")[0]}
                                </div>
                              )}
                            </div>
                            <span className="font-weight-bold text-dark">
                              {item.item_details?.name || "Unknown Item"}
                            </span>
                          </div>
                        </td>
                        <td className="align-middle">
                          <span className="badge badge-light-primary font-weight-bold">
                            {item.category ||
                              item.item_details?.category ||
                              "Uncategorized"}
                          </span>
                        </td>
                        <td
                          style={{ textAlign: "right" }}
                          className="font-weight-bold text-dark-75 align-middle"
                        >
                          {(item.soldQty || 0).toLocaleString()}
                        </td>
                        <td
                          style={{ textAlign: "right" }}
                          className="font-weight-bold text-primary align-middle"
                        >
                          {toPesoAmount(item.totalSales || 0)}
                        </td>
                        <td
                          style={{ textAlign: "right" }}
                          className="font-weight-bold text-muted align-middle"
                        >
                          {sharePct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-muted">
                      No items found.
                    </td>
                  </tr>
                )}
              </tbody>
              {filteredAndSortedItems.length > 0 && (
                <tfoot className="font-weight-bold bg-light">
                  <tr>
                    <td colSpan="3" className="text-right">
                      Total:
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {filteredAndSortedItems
                        .reduce((acc, cur) => acc + (cur.soldQty || 0), 0)
                        .toLocaleString()}
                    </td>
                    <td
                      style={{ textAlign: "right" }}
                      className="text-primary font-weight-bolder"
                    >
                      {toPesoAmount(
                        filteredAndSortedItems.reduce(
                          (acc, cur) => acc + toNum(cur.totalSales),
                          0,
                        ),
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {searchTerm
                        ? `${(
                            (filteredAndSortedItems.reduce(
                              (acc, cur) => acc + (cur.totalSales || 0),
                              0,
                            ) /
                              (totalSalesAll || 1)) *
                            100
                          ).toFixed(1)}%`
                        : "100.0%"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </PerfectScrollbar>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <span className="text-muted font-size-sm">
          Showing {filteredAndSortedItems.length} of {items.length} items
        </span>
        <div>
          <Button
            variant="outline-primary"
            className="mr-2 font-weight-bold"
            onClick={() => onExport && onExport(filteredAndSortedItems)}
            disabled={filteredAndSortedItems.length === 0 || isLoading}
          >
            Generate Report
          </Button>
          <Button
            variant="secondary"
            className="font-weight-bold"
            onClick={onHide}
          >
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
